/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Button,
  Dropdown,
  type OptionType,
  type SingleValue,
} from '@omnipresentgroup/design-system';
import { useXeroAccounts } from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  type CellContext,
  type ColumnDef,
  type ColumnMeta,
  createColumnHelper,
} from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { useManagerUser } from '../../../hooks';
import type { XeroAccountCodeMappingExtendedItem } from '../types';

export type TableMetaType = {
  updateXeroAccountCode: (
    newXeroAccountCode: XeroAccountCodeMappingExtendedItem['xeroAccountCode'],
    rowIndex?: number,
  ) => void;
};

type ColumnMetaType = ColumnMeta<XeroAccountCodeMappingExtendedItem, any> & {
  options?: OptionType[];
};

export enum ColumnHeaders {
  category = 'Invoice category (tax rate)',
  xeroAccount = 'Xero account (tax rate)',
  applyAll = '',
}

const validTaxRate = (taxRate: string | undefined): boolean =>
  taxRate !== undefined && taxRate !== '';

export const useAccountCodesTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<XeroAccountCodeMappingExtendedItem, unknown>[];
}) => {
  const { companyId } = useManagerUser();
  const { data: xeroAccounts } = useXeroAccounts({
    clientId: companyId,
  });

  const EditableSelectCell = useCallback(
    ({
      row,
      column,
      table,
    }: CellContext<XeroAccountCodeMappingExtendedItem, any>) => {
      const xeroAccountCodeId = row.original.xeroAccountCode?.id;
      const columnMeta = column.columnDef.meta as ColumnMetaType;
      const tableMeta = table.options.meta as TableMetaType;

      return (
        <Dropdown
          id="xero-account-codes-dropdown"
          options={columnMeta.options ?? []}
          value={columnMeta.options?.find(
            (option) => option.value === xeroAccountCodeId,
          )}
          errorMessage={row.original.error?.description}
          isSearchable
          onChange={(selectedOption) => {
            const selectedXeroAccountCodeId = (
              selectedOption as SingleValue<OptionType>
            )?.value as string;
            const newXeroAccountCode = xeroAccounts?.find(
              ({ id }) => id === selectedXeroAccountCodeId,
            );
            tableMeta?.updateXeroAccountCode(newXeroAccountCode, row.index);
          }}
          size="small"
          warningMessage={row.original.warning}
        />
      );
    },
    [xeroAccounts],
  );

  const ApplyToAllCell = ({
    row,
    table,
  }: CellContext<XeroAccountCodeMappingExtendedItem, any>) => {
    const tableMeta = table.options.meta as TableMetaType;

    return (
      <Button
        icon="Copy"
        variant="secondary"
        size="small"
        disabled={!row.original.xeroAccountCode}
        onClick={() => {
          tableMeta &&
            row.original.xeroAccountCode &&
            tableMeta?.updateXeroAccountCode(row.original.xeroAccountCode);
        }}
      >
        Apply to all
      </Button>
    );
  };

  const columnHelper = createColumnHelper<XeroAccountCodeMappingExtendedItem>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<XeroAccountCodeMappingExtendedItem, any>[] =
    useMemo(() => {
      const baseColumns = [
        columnHelper.accessor('category', {
          cell: (info) => {
            const { category } = info.row.original;
            const taxRate = category.taxRate;
            const categoryName = toSentenceCase(category.name);

            return validTaxRate(taxRate)
              ? `${categoryName} (${taxRate}%)`
              : categoryName;
          },
          header: () => ColumnHeaders.category,
          sortingFn: (rowA, rowB) =>
            rowA.original.category.name.localeCompare(
              rowB.original.category.name,
            ),
        }),
        columnHelper.accessor('xeroAccountCode', {
          cell: EditableSelectCell,
          header: () => ColumnHeaders.xeroAccount,
          meta: {
            options:
              xeroAccounts?.map(({ id, name, code, taxRate }) => ({
                value: id,
                label: validTaxRate(taxRate)
                  ? `${code} - ${toSentenceCase(name)} (${taxRate}%)`
                  : `${code} - ${toSentenceCase(name)}`,
              })) ?? [],
          },
          sortingFn: (rowA, rowB) => {
            const xeroAccountA = rowA.original.xeroAccountCode?.code ?? '';
            const xeroAccountB = rowB.original.xeroAccountCode?.code ?? '';
            return xeroAccountA.localeCompare(xeroAccountB);
          },
          size: 300,
        }),
        columnHelper.display({
          id: 'applyAll',
          cell: ApplyToAllCell,
          header: () => ColumnHeaders.applyAll,
          meta: {
            style: {
              textAlign: 'right' as const,
            },
          },
        }),
      ];

      return columnsOverride
        ? [...baseColumns, ...columnsOverride]
        : baseColumns;
    }, [EditableSelectCell, columnHelper, columnsOverride, xeroAccounts]);

  return {
    columns,
    columnHelper,
  };
};
